/**
 * The BUBBLE stream's numbers (spec 02 §4, §7.1, §7.2, §7.7, §8), each copied from the rebb64 routine
 * that uses it. Positions are the C64's own (see README.md): X, Y in the VIC sprite frame; `row` is
 * the C64's bubble row, tile row + 3.
 */

/** Software-sprite slots: every entity loop runs x = $11..0 (D_0CF2, D_13BE, D_E90E). */
export const SLOTS = 18;

// --- The shot (D_2301 L232A, L0F48..L100B) --------------------------------------------------------
/** $A77D = $88: 8 steps, bit 7 marking the first (a wall there bursts the bubble). */
export const SHOT_STEPS = 0x88;
/** With purple candy (D_2DB2): $8C, 12 steps. */
export const SHOT_STEPS_LONG = 0x8c;
/** Each step moves X by 8 and the column by 1 (L0F98). */
export const SHOT_STEP_PX = 8;
/** The rightmost column a shot may occupy (L0FD1: `cmp #$1C`). */
export const SHOT_MAX_COL = 0x1c;
/** L105B: a monster within |dx| < 16, |dy| < 16 of a shot step is trapped. */
export const TRAP_RANGE = 16;

// --- Timers (D_13BE) --------------------------------------------------------------------------------
/** A blown bubble's life, set when it is made (L2366: $A9FA = $7D). */
export const BUBBLE_LIFE = 0x7d;
/** A plain or special bubble from a hole (D_0CC5: $A9FA = $82). */
export const HOLE_BUBBLE_LIFE = 0x82;
/** A trapped monster escapes when this runs out (L10C4: $A9FA = $A0). */
export const TRAP_TIME = 0xa0;
/** Timer values below this, when even, toggle the blink / the trapped monster's flash (L1413: $11). */
export const BLINK_BELOW = 0x11;
/** When fewer than this many slots are free (types >= $34 count as free), two plain bubbles burst (L1449). */
export const CROWD_MIN_FREE = 2;
export const CROWD_POPS = 2;

// --- Floating (D_0CF2 L0D02..L0EC2) -----------------------------------------------------------------
/** L0D02: a player touches a floating bubble when |(Px + 2) - Bx| < 16 and |Py - By| < 16. */
export const TOUCH_X_BIAS = 2;
export const TOUCH_RANGE = 16;
/** L0D86: on rand < $EA a bubble checks for a neighbour within 16 px (bunching). */
export const BUNCH_CHANCE = 0xea;
export const BUNCH_RANGE = 16;
/** The bunching and wind clamps (L0DC7..L0DF6, L0E00): rows 4..$1C, columns 2..$1B. */
export const ROW_MIN = 4;
export const ROW_MAX = 0x1c;
export const COL_MIN = 2;
export const COL_MAX = 0x1c;
/** L0E23: 2 px per M-tick. */
export const FLOAT_PX = 2;
/** Going up past row 0 lands on row $1A; going down to row $1D lands on row 0 (L0E3B, L0E98). */
export const WRAP_TO_BOTTOM = 0x1a;
export const WRAP_AT_BOTTOM = 0x1d;

// --- Bursting (routine_3D2D, routine_3DB0, L_E97F) ---------------------------------------------------
/** routine_3D2D: a bursting bubble bursts every bubble within |dx| < 24 and |dy| < 24. */
export const CHAIN_RANGE = 0x18;
/** routine_3DB0 ($3DC0): stored units for a burst: 1 (10 shown), types $06..$0B 10 (100 shown). */
export const POP_UNITS = 1;
export const SPECIAL_POP_UNITS = 10;
/** Table $AB52 (BCD, added one byte up = x 100 units) by monsters killed in the chain, paid when it ends. */
export const CHAIN_BONUS_UNITS: readonly number[] = [0, 100, 200, 400, 800, 1000, 2000, 4000, 8000];

// --- The hole spawner (D_0BED, D_3A6C) --------------------------------------------------------------
/** D_0BF5: the timer is reset to $16, so an attempt comes every 23 M-ticks. */
export const SPAWN_PERIOD = 0x16;
/** The self-modified timer's assembled value: the first attempt comes on the 2nd pass. */
export const SPAWN_FIRST = 1;
/** D_0BFE: rand < $28 skips the attempt (40/256). */
export const SPAWN_SKIP = 0x28;
/** D_0C65: no hole bubble while 10 specials (types $06..$0B) float. */
export const MAX_SPECIALS = 10;
/** Spawn columns (X = col x 8 + $14), and the map cells tested (D_850A, D_8514, D_88CA, D_88D4). */
export const HOLE_COLS: readonly [number, number] = [0x0b, 0x14];
export const HOLE_TEST_COLS: readonly [number, number] = [10, 20];
/** Spawn rows (Y = row x 8 + $15): above the top wall, below the bottom wall. */
export const HOLE_ROW_TOP = 0;
export const HOLE_ROW_BOTTOM = 0x1c;

// --- Death items (D_1A6F, L267B, D_273D, score_display, D_29BB) ---------------------------------------
/** Life after landing: $87A0 = $C8 I-ticks (score_display $2921). */
export const ITEM_LIFE = 0xc8;
/** D_29C0: a player within |dx| < 16 and |dy| < 16 collects it. */
export const ITEM_RANGE = 16;
/** The death flight: speeds rand(8) + 1, the step timer 7 then every 4 I-ticks (D_1A6F, L267B). */
export const FLIGHT_TIMER = 7;
export const FLIGHT_PERIOD = 3;
/** The flight bounces off X $24 and $F4 (L_26BF). */
export const FLIGHT_X_MIN = 0x24;
export const FLIGHT_X_MAX = 0xf4;
/** The fall after it: 2 px per I-tick, wrapping $F5 -> $15 (D_273D). */
export const FALL_PX = 2;
export const WRAP_Y_BOTTOM = 0xf5;
export const WRAP_Y_TOP = 0x15;
/** The landing test runs from Y $1F, on Y aligned to the tile grid (D_273D L_274C). */
export const LAND_MIN_Y = 0x1f;

// --- The water stream (D_1B40, spawn_position_init $284D) ---------------------------------------------
/** 11 cells ($A74B / $A756), cell 0 the tail; cell 9 carries; 10 are drawn (cells 1..10). */
export const WATER_CELLS = 11;
export const WATER_CARRY_CELL = 9;
/** A sprite within |dx| < 12 and |dy| < 12 of the carrying cell is caught (L_1BE3). */
export const WATER_RANGE = 12;
/** Row $1D: past the bottom; the stream ends when its tail gets there. */
export const WATER_END_ROW = 0x1d;
/** Carried sprites sit at X = col x 8 + $14, Y = row x 8 + 5; Y $ED is the bottom. */
export const WATER_Y_OFFSET = 5;
export const WATER_BOTTOM_Y = 0xed;
/** A player reaching the bottom is set down at X $64, or $B4 when X >= $94 ($287C). */
export const WATER_DROP_X: readonly [number, number] = [0x64, 0xb4];
export const WATER_DROP_SPLIT = 0x94;

// --- Fire (routine_3DB0 $3E5E, L11B7, D_1319 - misnamed update_player_sprites) ----------------------
/** L11B7: the drop's sub-row toggles 0 / 4, the row stepping on the 4: 4 px a pass. */
export const DROP_STEP = 4;
/** L11B7: a drop reaching row $1D (past the bottom) vanishes. */
export const DROP_END_ROW = 0x1d;
/** D_A770: five burning lines; a drop landing with none free burns nothing (L11E7). */
export const FIRE_LINES = 5;
/** L11F2: each side's reach is rand & 15; a right reach taking the column to $1E or more is dropped. */
export const FIRE_REACH_MASK = 0x0f;
export const FIRE_RIGHT_LIMIT = 0x1e;
/** L11F2 ($126C): a line is lit with its timer at 6. */
export const FIRE_TIMER = 6;
/**
 * D_ACBD, by the timer before its step: the character D_1319 draws - $1C low flames, $40 / $41 tall ones
 * (which kill, check_collision $1372), 0 erased. The timer steps when D_597F & 3 is 0: D_597F is sprite
 * 5's padding byte, 0 at load (dead-bytes registry) and written only by the potion stage and EXTEND,
 * which count it down to 0 - so in play it steps every pass: drawn 5 passes, deadly 3 (not 02 §8's
 * "about 20 / 12", which assumed a frame counter there).
 */
export const FIRE_CHARS: readonly number[] = [0x00, 0x00, 0x1c, 0x40, 0x41, 0x40, 0x1c];
export const FIRE_DEADLY = 0x40;
/** check_collision: a monster dies when the line's Y (row x 8 + $15) is exactly 16 below its own. */
export const FIRE_KILL_DY = 0x10;

// --- Bolts (routine_3DB0 $3E2C, L23B8, $10D3 / $10D6, special_item_handler $1134) -------------------
/** $10D6: 8 px and one column a pass; gone on reaching column $1E (right) or 0 (left). */
export const BOLT_STEP_PX = 8;
export const BOLT_END_RIGHT = 0x1e;
export const BOLT_END_LEFT = 0;
/** Level 100's index ($63): its boss check replaces the hole spawner ($2C7A), the potion code runs (D_7F53). */
export const LEVEL_100 = 0x63;
/** D_23DE: the lightning potion marks a blown bubble with the timer 8 + player; D_0F73's test is < $0A. */
export const POTION_TIMER = 0x08;
export const POTION_BELOW = 0x0a;

// --- The level-clear bonus ($171B, L0EC5, D_1E2E, D_2ACB, bonus-round.s) -------------------------------
/** L0EC5: a player within 16 px of the food's cell takes it for $70 (BCD) units: 700 shown. */
export const BUBBLE_FOOD_UNITS = 70;
export const BUBBLE_FOOD_RANGE = 16;
/** L0EC5: food falling to row $1D vanishes. */
export const BUBBLE_FOOD_END_ROW = 0x1d;
/**
 * The picture of the bubble food on the scheduled levels (player-state.s $067F): food index
 * 2 x level index + 9, less 46 until it is below 47.
 */
export function scheduledFood(levelIndex: number): number {
  let f = (levelIndex * 2 + 9) & 0xff;
  while (f >= 47) f -= 46;
  return f;
}
/**
 * The staffs and boxes (D_2D65 entries 1, 2, 21, 23, 25-27 -> D_348A, D_3495, D_348D, D_3498, D_34FA,
 * D_3502, D_350A): $68 the food the bubbles turn into (and the giant's picture, D_A892), ARG the giant's
 * value in BCD thousands (added one byte up: x 100 units), $6A its sprite set and colour ($A81F: yellow
 * cupcake, green melon, yellow / light-blue / purple diamond).
 */
export const GIANT_BONUS: Readonly<Record<number, { food: number; units: number; colour: number }>> = {
  1: { food: 0x1b, units: 1000, colour: 0x07 },
  2: { food: 0x18, units: 2000, colour: 0x05 },
  21: { food: 0x1b, units: 2000, colour: 0x07 },
  23: { food: 0x18, units: 3000, colour: 0x05 },
  25: { food: 0x29, units: 3000, colour: 0x07 },
  26: { food: 0x2b, units: 4000, colour: 0x0e },
  27: { food: 0x2c, units: 5000, colour: 0x04 },
};
/** D_2ACB: the giant's 2x2 sprites at X $80 / $98; its top from Y 0 down 4 px an I-tick to $C8. */
export const GIANT_X = 0x80;
export const GIANT_STEP = 4;
export const GIANT_LAND_Y = 0xc8;
/** L_2AFB: collected once landed by a player with 0 <= X - $80 < $30 and 0 <= Y - $C8 < $24. */
export const GIANT_W = 0x30;
export const GIANT_H = 0x24;

// --- EXTEND's screenful of food (extend-bonus.s L_3389) ---------------------------------------------
/**
 * L_338D: a food rand & 31, drawn again while its picture (D_A892) is $28 or more - so only these; laid
 * 2x2 over tile rows 0-23 and all 32 columns. Pictures only: nobody can take them.
 */
export const EXTEND_FOODS: readonly number[] = [3, 7, 8, 9, ...Array.from({ length: 19 }, (_, i) => 12 + i)];
/** L_32EE / L_331A: the food comes 20 x 5 + 15 frames after the fanfare starts (the letters' scramble). */
export const EXTEND_FOOD_FRAMES = 115;
